<template>
  <div class="announcement-management">
    <div class="header">
      <h2>公告管理</h2>
      <div class="actions">
        <el-button type="primary" @click="handleAdd">
          <el-icon><Plus /></el-icon>
          发布公告
        </el-button>
        <el-button @click="fetchList">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 搜索和筛选 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="searchForm">
        <el-form-item label="标题">
          <el-input v-model="searchForm.title" placeholder="请输入标题关键词" clearable />
        </el-form-item>
        <el-form-item label="类型">
          <el-select v-model="searchForm.type" placeholder="请选择类型" clearable>
            <el-option label="通知" value="notice" />
            <el-option label="文章" value="article" />
            <el-option label="指南" value="guide" />
            <el-option label="新闻" value="news" />
          </el-select>
        </el-form-item>
        <el-form-item label="发布状态">
          <el-select v-model="searchForm.isPublished" placeholder="请选择状态" clearable>
            <el-option label="已发布" :value="true" />
            <el-option label="未发布" :value="false" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            搜索
          </el-button>
          <el-button @click="resetSearch">
            <el-icon><Refresh /></el-icon>
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 公告列表 -->
    <el-card>
      <el-table :data="dataList" v-loading="loading" stripe border>
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="title" label="标题" min-width="200">
          <template #default="{ row }">
            <div class="title-cell">
              <el-tag v-if="row.isTop" type="danger" size="small" class="top-tag">置顶</el-tag>
              <span>{{ row.title }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="type" label="类型" width="100">
          <template #default="{ row }">
            <el-tag :type="getTypeTag(row.type)">
              {{ getTypeText(row.type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="isPublished" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.isPublished ? 'success' : 'info'">
              {{ row.isPublished ? '已发布' : '未发布' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="viewCount" label="浏览量" width="90" />
        <el-table-column prop="likeCount" label="点赞数" width="90" />
        <el-table-column prop="sortOrder" label="排序" width="80" />
        <el-table-column prop="publishedAt" label="发布时间" width="160">
          <template #default="{ row }">
            {{ formatDate(row.publishedAt) || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="160">
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleView(row)">
              查看
            </el-button>
            <el-button type="warning" size="small" @click="handleEdit(row)">
              编辑
            </el-button>
            <el-button 
              v-if="!row.isPublished" 
              type="success" 
              size="small" 
              @click="handlePublish(row)"
            >
              发布
            </el-button>
            <el-button 
              v-else 
              type="info" 
              size="small" 
              @click="handleUnpublish(row)"
            >
              下架
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="pagination.current"
          v-model:page-size="pagination.size"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 添加/编辑对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="800px"
      @close="resetForm"
    >
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="标题" prop="title">
              <el-input v-model="form.title" placeholder="请输入标题" maxlength="200" show-word-limit />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="类型" prop="type">
              <el-select v-model="form.type" placeholder="请选择类型">
                <el-option label="通知" value="notice" />
                <el-option label="文章" value="article" />
                <el-option label="指南" value="guide" />
                <el-option label="新闻" value="news" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="封面图" prop="coverImage">
          <el-input v-model="form.coverImage" placeholder="请输入封面图URL（可选）" />
        </el-form-item>
        <el-form-item label="内容" prop="content">
          <el-input 
            v-model="form.content" 
            type="textarea" 
            :rows="10" 
            placeholder="请输入内容" 
          />
        </el-form-item>
        <el-form-item label="标签" prop="tags">
          <el-select 
            v-model="form.tags" 
            multiple 
            filterable 
            allow-create 
            default-first-option
            placeholder="输入标签后回车添加"
          >
          </el-select>
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="排序" prop="sortOrder">
              <el-input-number v-model="form.sortOrder" :min="0" :max="9999" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="置顶" prop="isTop">
              <el-switch v-model="form.isTop" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="立即发布" prop="isPublished">
              <el-switch v-model="form.isPublished" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitting">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="公告详情"
      width="700px"
    >
      <div v-if="currentItem" class="detail-content">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="ID">{{ currentItem.id }}</el-descriptions-item>
          <el-descriptions-item label="类型">
            <el-tag :type="getTypeTag(currentItem.type)">{{ getTypeText(currentItem.type) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="标题" :span="2">{{ currentItem.title }}</el-descriptions-item>
          <el-descriptions-item label="封面图" :span="2" v-if="currentItem.coverImage">
            <el-image :src="currentItem.coverImage" style="max-width: 200px; max-height: 150px;" fit="cover" />
          </el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="currentItem.isPublished ? 'success' : 'info'">
              {{ currentItem.isPublished ? '已发布' : '未发布' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="置顶">
            <el-tag :type="currentItem.isTop ? 'danger' : 'info'">
              {{ currentItem.isTop ? '是' : '否' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="浏览量">{{ currentItem.viewCount || 0 }}</el-descriptions-item>
          <el-descriptions-item label="点赞数">{{ currentItem.likeCount || 0 }}</el-descriptions-item>
          <el-descriptions-item label="排序">{{ currentItem.sortOrder || 0 }}</el-descriptions-item>
          <el-descriptions-item label="标签">
            <el-tag v-for="tag in currentItem.tags" :key="tag" size="small" class="mr-5">{{ tag }}</el-tag>
            <span v-if="!currentItem.tags || currentItem.tags.length === 0">无</span>
          </el-descriptions-item>
          <el-descriptions-item label="发布时间">{{ formatDate(currentItem.publishedAt) || '-' }}</el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ formatDate(currentItem.createdAt) }}</el-descriptions-item>
        </el-descriptions>
        <div class="content-section">
          <h4>内容</h4>
          <div class="content-text">{{ currentItem.content }}</div>
        </div>
      </div>
      <template #footer>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="handleEditFromDetail">编辑</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Refresh, Search } from '@element-plus/icons-vue'
import { announcementApi } from '@/api/api.js'

// 状态
const loading = ref(false)
const dialogVisible = ref(false)
const detailDialogVisible = ref(false)
const isEditMode = ref(false)
const submitting = ref(false)
const formRef = ref()
const currentItem = ref(null)

// 搜索表单
const searchForm = reactive({
  title: '',
  type: '',
  isPublished: null
})

// 表单
const form = reactive({
  title: '',
  content: '',
  type: 'notice',
  coverImage: '',
  isTop: false,
  isPublished: false,
  sortOrder: 0,
  tags: []
})

// 表单验证规则
const rules = {
  title: [
    { required: true, message: '请输入标题', trigger: 'blur' },
    { max: 200, message: '标题最多200个字符', trigger: 'blur' }
  ],
  content: [
    { required: true, message: '请输入内容', trigger: 'blur' }
  ],
  type: [
    { required: true, message: '请选择类型', trigger: 'change' }
  ]
}

// 分页
const pagination = reactive({
  current: 1,
  size: 10,
  total: 0
})

// 数据列表
const dataList = ref([])

// 计算属性
const dialogTitle = computed(() => isEditMode.value ? '编辑公告' : '发布公告')

// 方法
const getTypeText = (type) => {
  const map = {
    notice: '通知',
    article: '文章',
    guide: '指南',
    news: '新闻'
  }
  return map[type] || type
}

const getTypeTag = (type) => {
  const map = {
    notice: 'warning',
    article: 'success',
    guide: 'primary',
    news: 'danger'
  }
  return map[type] || ''
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN')
}

// 获取列表
const fetchList = async () => {
  loading.value = true
  try {
    const params = {
      page: pagination.current,
      limit: pagination.size,
      ...Object.fromEntries(
        Object.entries(searchForm).filter(([_, v]) => v !== '' && v !== null)
      )
    }
    const response = await announcementApi.getAdminList(params)
    
    if (response.items) {
      dataList.value = response.items || []
      pagination.total = response.total || dataList.value.length
    } else if (Array.isArray(response)) {
      dataList.value = response
      pagination.total = response.length
    }
  } catch (error) {
    console.error('获取公告列表失败:', error)
    ElMessage.error('获取公告列表失败')
    dataList.value = []
    pagination.total = 0
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  pagination.current = 1
  fetchList()
}

const resetSearch = () => {
  Object.keys(searchForm).forEach(key => {
    searchForm[key] = key === 'isPublished' ? null : ''
  })
  handleSearch()
}

const handleSizeChange = (size) => {
  pagination.size = size
  fetchList()
}

const handleCurrentChange = (page) => {
  pagination.current = page
  fetchList()
}

const handleAdd = () => {
  isEditMode.value = false
  dialogVisible.value = true
}

const handleEdit = (row) => {
  isEditMode.value = true
  Object.assign(form, {
    id: row.id,
    title: row.title,
    content: row.content,
    type: row.type,
    coverImage: row.coverImage || '',
    isTop: row.isTop || false,
    isPublished: row.isPublished || false,
    sortOrder: row.sortOrder || 0,
    tags: row.tags || []
  })
  dialogVisible.value = true
}

const handleView = (row) => {
  currentItem.value = row
  detailDialogVisible.value = true
}

const handleEditFromDetail = () => {
  if (currentItem.value) {
    detailDialogVisible.value = false
    handleEdit(currentItem.value)
  }
}

const handlePublish = async (row) => {
  try {
    await announcementApi.update(row.id, { isPublished: true, publishedAt: new Date() })
    ElMessage.success('发布成功')
    fetchList()
  } catch (error) {
    console.error('发布失败:', error)
    ElMessage.error('发布失败')
  }
}

const handleUnpublish = async (row) => {
  try {
    await announcementApi.update(row.id, { isPublished: false })
    ElMessage.success('已下架')
    fetchList()
  } catch (error) {
    console.error('下架失败:', error)
    ElMessage.error('下架失败')
  }
}

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm('确定要删除该公告吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    await announcementApi.delete(row.id)
    ElMessage.success('删除成功')
    fetchList()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除失败:', error)
      ElMessage.error('删除失败')
    }
  }
}

const resetForm = () => {
  if (formRef.value) {
    formRef.value.resetFields()
  }
  Object.assign(form, {
    title: '',
    content: '',
    type: 'notice',
    coverImage: '',
    isTop: false,
    isPublished: false,
    sortOrder: 0,
    tags: []
  })
}

const handleSubmit = async () => {
  if (!formRef.value) return
  
  try {
    await formRef.value.validate()
    submitting.value = true
    
    const data = { ...form }
    if (data.isPublished && !isEditMode.value) {
      data.publishedAt = new Date()
    }
    
    if (isEditMode.value) {
      await announcementApi.update(form.id, data)
      ElMessage.success('更新成功')
    } else {
      await announcementApi.create(data)
      ElMessage.success('创建成功')
    }
    
    dialogVisible.value = false
    fetchList()
    resetForm()
  } catch (error) {
    console.error('保存失败:', error)
    ElMessage.error(error.message || '保存失败')
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  fetchList()
})
</script>

<style scoped>
.announcement-management {
  padding: 0;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  font-size: 20px;
  color: #ffffff;
}

.actions {
  display: flex;
  gap: 10px;
}

.filter-card {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.title-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.top-tag {
  flex-shrink: 0;
}

.detail-content {
  padding: 10px 0;
}

.content-section {
  margin-top: 20px;
}

.content-section h4 {
  margin-bottom: 10px;
  color: #606266;
}

.content-text {
  padding: 15px;
  background: #f5f7fa;
  border-radius: 4px;
  white-space: pre-wrap;
  line-height: 1.6;
}

.mr-5 {
  margin-right: 5px;
}
</style>
